import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { StorageAPI } from '../lib/api/storage';
import { toastMutate } from '../lib/query/toast-mutation';
import type { StorageFileResponse, StorageFolderResponse } from '../lib/api/storage';
import { queryKeys } from '../lib/query/client';
import { useCurrentOrg } from './useCurrentOrg';

export type StorageItem =
  | (StorageFolderResponse & { itemType: 'folder' })
  | (StorageFileResponse & { itemType: 'file' });

export function useStorageFiles(folderId?: string | null, options?: { enabled?: boolean }) {
  const { currentOrg } = useCurrentOrg();
  const orgId = currentOrg?.id ?? '';

  const externalEnabled = options?.enabled ?? true;

  return useQuery({
    queryKey: queryKeys.storageFiles(orgId, folderId),
    queryFn: async (): Promise<StorageItem[]> => {
      const [files, folders] = await Promise.all([
        StorageAPI.listFiles(folderId),
        StorageAPI.listFolders(folderId),
      ]);
      const folderItems: StorageItem[] = folders.map((f) => ({
        ...f,
        itemType: 'folder' as const,
      }));
      const fileItems: StorageItem[] = files
        .filter((f) => f.uploadStatus === 'complete')
        .map((f) => ({ ...f, itemType: 'file' as const }));
      return [...folderItems, ...fileItems];
    },
    enabled: !!orgId && externalEnabled,
  });
}

export function useStorageQuota() {
  const { currentOrg } = useCurrentOrg();
  const orgId = currentOrg?.id ?? '';

  return useQuery({
    queryKey: queryKeys.storageQuota(orgId),
    queryFn: () => StorageAPI.getQuota(),
    enabled: !!orgId,
  });
}

export function useDeleteFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: string) => {
      const result = await toastMutate(StorageAPI.deleteFile(fileId), {
        loading: 'Deleting file...',
        success: 'File deleted',
      });
      await queryClient.refetchQueries({ queryKey: queryKeys.storage() });
      return result;
    },
  });
}

export function useDeleteFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (folderId: string) => {
      const result = await toastMutate(StorageAPI.deleteFolder(folderId), {
        loading: 'Deleting folder...',
        success: 'Folder deleted',
      });
      await queryClient.refetchQueries({ queryKey: queryKeys.storage() });
      return result;
    },
  });
}

export function useCreateFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; parentId?: string | null }) => {
      const result = await toastMutate(StorageAPI.createFolder(data), {
        loading: 'Creating folder...',
        success: 'Folder created',
      });
      await queryClient.refetchQueries({ queryKey: queryKeys.storage() });
      return result;
    },
  });
}

export function useRenameItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      name,
      itemType,
    }: {
      id: string;
      name: string;
      itemType: 'file' | 'folder';
    }) => {
      const result = await toastMutate<StorageFileResponse | StorageFolderResponse>(
        itemType === 'file'
          ? StorageAPI.updateFile(id, { name })
          : StorageAPI.updateFolder(id, { name }),
        { loading: 'Renaming...', success: 'Renamed successfully' },
      );
      await queryClient.refetchQueries({ queryKey: queryKeys.storage() });
      return result;
    },
  });
}
